/**
 * MillageInfoInputs - Core Component (Production-Ready)
 * Clean input component for millage configuration without demo UI chrome.
 * Used in: Project workflow pages
 *
 * For demo page with collapsible UI, see MillageInfoInputsDemoWrapper.tsx
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DOMAIN KNOWLEDGE: Millage Rate Configuration (Michigan Municipal Bonds)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This component configures tax rates over the bond's life, balancing voter
 * acceptability with financial flexibility for future projects and economic uncertainties.
 *
 * Key Concepts:
 *
 * 1. PEAK MILLAGE RATE
 *    - Highest tax rate during the bond's life
 *    - Political/financial decision: maintain current rate (easier voter approval)
 *      or propose acceptable new rate
 *    - Must be greater than minimum millage
 *    - Range: 0-20 mills (Michigan limit)
 *    - Determines maximum revenue capacity
 *
 * 2. PEAK MILLAGE DURATION
 *    - Number of years peak millage is maintained
 *    - Typical range: 3-15 years
 *    - Longer duration = more revenue capacity but less flexibility
 *    - Shorter duration = less revenue but more capacity for future projects
 *    - Maximum: 10 years (validation limit)
 *
 * 3. MINIMUM MILLAGE RATE
 *    - Lowest tax rate at the end of the bond's life
 *    - "Gut feel" endpoint chosen by advisors
 *    - Must be less than peak millage
 *    - Range: 0-20 mills (Michigan limit)
 *    - Creates capacity for future bond projects
 *
 * 4. MINIMUM MILLAGE DURATION
 *    - Number of years minimum millage is maintained
 *    - Determines how long the rate stays at minimum
 *    - Maximum: 10 years (validation limit)
 *
 * 5. MILLAGE DESCENT REQUIREMENT
 *    - Rate must descend over time (never increase)
 *    - Creates capacity for future bond projects
 *    - Absorbs slower-than-expected revenue growth
 *    - Feeds into 53 predefined millage shapes (logarithmic descent)
 *
 * Why This Matters:
 * - Millage rates determine revenue from taxable values
 * - Revenue capacity DRIVES bond sizing (not the other way around)
 * - Peak millage is a voter approval constraint
 * - Descent pattern balances revenue needs with future flexibility
 * - Over-aggressive rates can lead to voter rejection
 *
 * Processing Pipeline Impact:
 * - Millage inputs feed into 53 predefined millage shapes
 * - Shapes generate candidates from least to most revenue
 * - Shapes ensure continuous descent while maximizing revenue capacity
 * - Revenue calculations use millage rates × taxable values
 *
 * Common Mistake:
 * ❌ Don't set peak millage ≤ minimum millage
 * ❌ Don't exceed 20 mills (Michigan limit)
 * ❌ Don't set durations > 10 years
 * ✅ Peak millage > minimum millage
 * ✅ Rates descend over time (never increase)
 * ✅ Use voter-acceptable rates for peak millage
 *
 * For More Context:
 * @see apps/web/src/builders/millage-info/_millage-info.md - Builder overview
 * @see docs/business-context/process-overview.md - How millage feeds into processing pipeline
 */

import { Box, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type React from 'react';

import { MillageInfoBuilder } from '~/builders/millage-info/MillageInfoBuilder';
import { NumberInput } from '~/components/inputs';
import { useSidebar } from '~/components/layout/shared/SidebarContext';
import { useSimpleInputValidation } from '~/hooks/useSimpleInputValidation';
import type { MillageInputs, MillageInputsForm } from '~/types';

interface MillageInfoInputsProps {
  values: MillageInputsForm;
  onUpdate: (values: MillageInputsForm) => void;
  onBlur?: () => void;
  showValidation?: boolean;
  isDisabled?: boolean;
}

/**
 * Convert form values to engine format for validation
 * Returns null if any required field is empty or invalid
 */
const convertFormToEngineForValidation = (formValues: MillageInputsForm): MillageInputs | null => {
  // Check all fields are present
  if (
    !formValues.peakMillage?.trim() ||
    !formValues.peakMillageDuration?.trim() ||
    !formValues.minimumMillage?.trim() ||
    !formValues.minimumMillageDuration?.trim()
  ) {
    return null;
  }

  // Parse and validate numeric values
  const peakMillage = parseFloat(formValues.peakMillage);
  const peakMillageDuration = parseFloat(formValues.peakMillageDuration);
  const minimumMillage = parseFloat(formValues.minimumMillage);
  const minimumMillageDuration = parseFloat(formValues.minimumMillageDuration);

  // Check if all values are valid numbers
  if (
    !Number.isFinite(peakMillage) ||
    !Number.isFinite(peakMillageDuration) ||
    !Number.isFinite(minimumMillage) ||
    !Number.isFinite(minimumMillageDuration)
  ) {
    return null;
  }

  return {
    peakMillage,
    peakMillageDuration,
    minimumMillage,
    minimumMillageDuration,
  };
};

export const MillageInfoInputs: React.FC<MillageInfoInputsProps> = ({
  values,
  onUpdate,
  onBlur,
  showValidation = false,
  isDisabled = false,
}) => {
  const { isExpanded } = useSidebar();

  // Use shared validation hook for consistency
  const { handlers, computed } = useSimpleInputValidation({
    values: values as unknown as Record<string, string>,
    onUpdate: onUpdate as unknown as (values: Record<string, string>) => void,
    onBlur,
    showValidation,
    requiredFields: [
      'peakMillage',
      'peakMillageDuration',
      'minimumMillage',
      'minimumMillageDuration',
    ],
    convertToEngine: convertFormToEngineForValidation as unknown as (
      formValues: Record<string, string>
    ) => MillageInputs | null,
    validate: (engineValues) => MillageInfoBuilder.validateInputs(engineValues),
    mapErrorsToFields: (error) => {
      // Map error messages to field names
      if (error.includes('Peak millage') && !error.includes('must be greater than minimum')) {
        return 'peakMillage';
      } else if (error.includes('Peak millage duration')) {
        return 'peakMillageDuration';
      } else if (error.includes('Minimum millage') && !error.includes('must be greater than')) {
        return 'minimumMillage';
      } else if (error.includes('Minimum millage duration')) {
        return 'minimumMillageDuration';
      }
      return null; // Unknown error (including relationship errors)
    },
    shouldShowError: (error, touched) => {
      // Handle relationship errors: show if either related field is touched
      if (error.includes('Peak millage must be greater than minimum')) {
        return touched.has('peakMillage') || touched.has('minimumMillage');
      }
      // For other errors, use standard field mapping
      return false; // Let mapErrorsToFields handle it
    },
  });

  const { handleInputChange, markTouched } = handlers;
  const { validationErrors, allFieldsTouched } = computed;

  return (
    <VStack
      align="stretch"
      boxShadow="md"
      gap={6}
      border="1px solid"
      borderColor="gray.200"
      p={4}
      borderRadius="md"
      bg={isDisabled ? 'gray.50' : undefined}
      pointerEvents={isDisabled ? 'none' : undefined}
      opacity={isDisabled ? 0.8 : 1}
    >
      <Heading size="md">Initial Millage Configuration</Heading>

      <SimpleGrid
        columns={{ base: 4, md: (isExpanded ? 2 : 4) as number }}
        gap={3}
        alignItems="flex-end"
      >
        <NumberInput
          label={isExpanded ? 'Peak Rate' : 'Peak Millage Rate'}
          value={values.peakMillage || ''}
          onChange={(value) => handleInputChange('peakMillage', value)}
          onBlur={() => markTouched('peakMillage')}
          min={0}
          max={20}
          step={0.01}
          precision={2}
          isRequired
        />

        <NumberInput
          label={isExpanded ? 'Peak Duration' : 'Peak Millage Duration'}
          value={values.peakMillageDuration || '1'}
          onChange={(value) => handleInputChange('peakMillageDuration', value)}
          onBlur={() => markTouched('peakMillageDuration')}
          min={1}
          max={25}
          step={1}
          isRequired
          formatOptions={{
            useGrouping: false,
            maximumFractionDigits: 0,
          }}
        />

        <NumberInput
          label={isExpanded ? 'Min Rate' : 'Minimum Millage Rate'}
          value={values.minimumMillage || ''}
          onChange={(value) => handleInputChange('minimumMillage', value)}
          onBlur={() => markTouched('minimumMillage')}
          min={0}
          max={20}
          step={0.01}
          precision={2}
          isRequired
        />

        <NumberInput
          label={isExpanded ? 'Min Duration' : 'Minimum Millage Duration'}
          value={values.minimumMillageDuration || '1'}
          onChange={(value) => handleInputChange('minimumMillageDuration', value)}
          onBlur={() => markTouched('minimumMillageDuration')}
          min={1}
          max={25}
          step={1}
          isRequired
          formatOptions={{
            useGrouping: false,
            maximumFractionDigits: 0,
          }}
        />
      </SimpleGrid>

      {/* Validation Messages */}
      {(showValidation || allFieldsTouched) && validationErrors.length > 0 && (
        <Box
          mt={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="red.500"
        >
          <Text fontSize="xs" fontWeight="bold" color="red.600" mb={2}>
            Please fix the following issues:
          </Text>
          <VStack align="flex-start" gap={1}>
            {validationErrors.map((error) => (
              <Text key={error} fontSize="xs" color="red.600">
                • {error}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

// Utility function to convert form values to engine format
export const convertToEngineFormat = (formValues: MillageInputsForm): MillageInputs => {
  return {
    peakMillage: parseFloat(formValues.peakMillage) || 0,
    peakMillageDuration: parseFloat(formValues.peakMillageDuration) || 0,
    minimumMillage: parseFloat(formValues.minimumMillage) || 0,
    minimumMillageDuration: parseFloat(formValues.minimumMillageDuration) || 0,
  };
};

// Utility function to convert engine format to form values
export const convertFromEngineFormat = (engineValues: MillageInputs): MillageInputsForm => {
  return {
    peakMillage: engineValues.peakMillage.toString(),
    peakMillageDuration: engineValues.peakMillageDuration.toString(),
    minimumMillage: engineValues.minimumMillage.toString(),
    minimumMillageDuration: engineValues.minimumMillageDuration.toString(),
  };
};

export type { MillageInputsForm };
